/**
 * The application shell: the layout containers, the toolbar, the per-mode panel columns and the
 * global keyboard shortcuts.
 *
 * The shell owns no editor state of its own. It reacts to `state.mode` by swapping which panels
 * are mounted into the left/right columns (body AND footer), and hands the viewport/timeline
 * containers back to the caller (`main.ts`) to fill — that is the whole contract between wave 3
 * and wave 4.
 *
 * Three structural facts worth knowing before editing this file:
 *  - a column is `div.column > (div.col-body + div.col-foot)`. `containers.left` is the SCROLLING
 *    body, and it keeps the id `left` — the survey harness and the tests address it by that id.
 *  - the timeline row exists only in ANIMATE (`.shell.no-timeline` elsewhere, audit A1), and the
 *    class is toggled synchronously from the store listener so the row never flashes.
 *  - the sizes of the two columns and the timeline are CSS custom properties owned by
 *    `installLayout` (`./layout.ts`), not by this module.
 */

import './panels/panels.css';

import type { EditorContext } from './context';
import type { Controllers } from './controllers/types';
import { dialogs } from './dialogs';
import type { EditorMode } from './index';
import { installLayout, type LayoutHandle, type LayoutStore } from './layout';
import { el } from './panels/dom';
import type { PanelHandle } from './panels/panel';
import { confirmDiscardDirtyClip, runAsync } from './panels/panel';
import {
  mountExportEmbedPanel,
  mountExportFooterPanel,
  mountExportPanel,
  mountHierarchyPanel,
  mountInspectorPanel,
  mountLibraryFilesPanel,
  mountLibraryPanel,
  mountMappingFooterPanel,
  mountMappingPanel,
  mountModelPanel,
  mountProjectListPanel,
  mountRigFooterPanel,
  mountRigPanel,
  mountSkinPanel,
  mountToolbar,
} from './panels';
import { mountNotifications } from './panels/notificationsPanel';
// The toolbar owns the "Save as" flow (dialog + controller call); the Ctrl+Shift+S branch below
// invokes that same function rather than a second copy of it.
import { promptSaveClipAs } from './panels/toolbarPanel';

export interface ShellContainers {
  toolbar: HTMLElement;
  /** The scrolling body of the left column (`#left`). */
  left: HTMLElement;
  viewport: HTMLElement;
  /** The scrolling body of the right column (`#right`). */
  right: HTMLElement;
  bottom: HTMLElement;
  /** Sticky footer of the left column — the mode's main action lives here, never scrolled away. */
  leftFooter: HTMLElement;
  rightFooter: HTMLElement;
}

export interface ShellHandle {
  containers: ShellContainers;
  layout: LayoutHandle;
  dispose(): void;
}

export interface ShellOptions {
  /** Where the column sizes are persisted. `main.ts` passes `settingsRepo`. */
  layoutStore?: LayoutStore;
}

type PanelMount = (
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
) => PanelHandle;

/** Left column per mode (SPEC "Editor Modes": only the controls of the current mode are shown). */
const LEFT_PANELS: Record<EditorMode, readonly PanelMount[]> = {
  model: [mountModelPanel],
  rig: [mountRigPanel, mountHierarchyPanel],
  map: [mountHierarchyPanel],
  animate: [mountHierarchyPanel],
  library: [mountLibraryPanel],
  export: [mountExportEmbedPanel],
};

const RIGHT_PANELS: Record<EditorMode, readonly PanelMount[]> = {
  model: [mountProjectListPanel],
  rig: [mountSkinPanel],
  map: [mountMappingPanel],
  animate: [mountInspectorPanel],
  library: [mountLibraryFilesPanel],
  export: [mountExportPanel],
};

/**
 * The sticky footer of each column, per mode: the ONE action that finishes the mode.
 *
 * A mode with no entry here leaves both footers empty, and an empty footer collapses to zero
 * height (`.col-foot:not(:has(.panel > *))` in `style.css`) rather than leaving a bar behind.
 */
interface FooterSlots {
  left?: readonly PanelMount[];
  right?: readonly PanelMount[];
}

const FOOTER_PANELS: Record<EditorMode, FooterSlots> = {
  model: {},
  rig: { left: [mountRigFooterPanel] },
  map: { right: [mountMappingFooterPanel] },
  animate: {},
  library: {},
  export: { right: [mountExportFooterPanel] },
};

/** How long the drop highlight survives the last `dragover` (there is no reliable "drag left"). */
const DROP_HINT_MS = 200;

/** Typing in a field — or answering a modal — must never be swallowed by an editor shortcut. */
function isTextEntry(target: EventTarget | null): boolean {
  // A modal dialog is a text-entry context as a whole: Ctrl+S while "Save clip as" is open must
  // not fire a second, different save behind it.
  if (typeof document !== 'undefined' && document.querySelector('dialog[open]')) return true;
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** The first dropped `.glb`, or null when the drag carried none. */
function firstGlb(files: FileList | undefined | null): File | null {
  if (!files) return null;
  for (const file of files) {
    if (file.name.toLowerCase().endsWith('.glb')) return file;
  }
  return null;
}

/**
 * Is this drag carrying FILES?
 *
 * The window handlers must not touch anything else. Calling `preventDefault` on every drag is
 * what made dragging a selection into the project-name field impossible — the browser's own
 * text-drop default never ran, and the editor answered a text drag with "Drop a .glb file".
 * `types` is a `DOMStringList` in some engines, so it is read through `Array.prototype`.
 */
function isFileDrag(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  return Array.prototype.includes.call(types, 'Files');
}

/** A text-entry element is the drop target's own business, files or not. */
function isDropTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return target.closest('input, textarea') !== null;
}

export function mountShell(
  rootEl: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
  options: ShellOptions = {},
): ShellHandle {
  const { store } = ctx;

  rootEl.replaceChildren();
  rootEl.classList.add('shell');

  const containers: ShellContainers = {
    toolbar: el('div', { id: 'toolbar' }),
    left: el('div', { id: 'left', class: 'col-body' }),
    viewport: el('div', { id: 'viewport' }),
    right: el('div', { id: 'right', class: 'col-body' }),
    bottom: el('div', { id: 'bottom' }),
    leftFooter: el('div', { id: 'left-footer', class: 'col-foot' }),
    rightFooter: el('div', { id: 'right-footer', class: 'col-foot' }),
  };

  rootEl.append(
    containers.toolbar,
    el('div', { class: 'column column-left' }, [containers.left, containers.leftFooter]),
    containers.viewport,
    el('div', { class: 'column column-right' }, [containers.right, containers.rightFooter]),
    containers.bottom,
  );

  // After the containers, so the splitter handles are the last children and sit above them.
  const layout = installLayout(rootEl, { store: options.layoutStore });

  const toolbar = mountToolbar(containers.toolbar, ctx, controllers);
  // Mounted before the viewport fills the same container: the layer is absolutely positioned and
  // `z-index: 5`, so the canvas that arrives later renders underneath it.
  const notifications = mountNotifications(containers.viewport, ctx);

  let mounted: PanelHandle[] = [];
  let mountedMode: EditorMode | null = null;
  let disposed = false;

  function mountColumns(mode: EditorMode): void {
    if (disposed || mode === mountedMode) return;
    for (const panel of mounted.splice(0)) panel.dispose();
    mountedMode = mode;
    for (const mount of LEFT_PANELS[mode]) mounted.push(mount(containers.left, ctx, controllers));
    for (const mount of RIGHT_PANELS[mode]) mounted.push(mount(containers.right, ctx, controllers));
    const footers = FOOTER_PANELS[mode];
    for (const mount of footers.left ?? []) {
      mounted.push(mount(containers.leftFooter, ctx, controllers));
    }
    for (const mount of footers.right ?? []) {
      mounted.push(mount(containers.rightFooter, ctx, controllers));
    }
  }

  /** DOM only — safe to run synchronously inside a store listener, and it must be: a deferred
   * class toggle would show one frame of the wrong grid. */
  function applyModeChrome(mode: EditorMode): void {
    rootEl.classList.toggle('no-timeline', mode !== 'animate');
    layout.setTimelineVisible(mode === 'animate');
  }

  applyModeChrome(store.getState().mode);
  mountColumns(store.getState().mode);

  // Deferred: the mode change arrives synchronously from inside the click handler of a toolbar
  // button that would otherwise be torn down mid-dispatch. The chrome above is not deferred —
  // it touches no element that is currently dispatching.
  const unsubscribeMode = store.select(
    (state) => state.mode,
    (mode) => {
      applyModeChrome(mode);
      queueMicrotask(() => mountColumns(mode));
    },
  );

  // --- window file drop -----------------------------------------------------
  //
  // The MODEL panel's own dropzone is a 200px box in one mode (audit B2); dropping a character
  // anywhere on the editor is what people actually try. The panel keeps working: its handler runs
  // first and the window handler skips any drop that landed inside a `.dropzone`.

  let dropTimer: ReturnType<typeof setTimeout> | null = null;

  function showDropHint(): void {
    rootEl.classList.add('dropping');
    if (dropTimer !== null) clearTimeout(dropTimer);
    dropTimer = setTimeout(() => {
      dropTimer = null;
      rootEl.classList.remove('dropping');
    }, DROP_HINT_MS);
  }

  function clearDropHint(): void {
    if (dropTimer !== null) clearTimeout(dropTimer);
    dropTimer = null;
    rootEl.classList.remove('dropping');
  }

  /**
   * Importing over an open project is destructive — it resets the rig, the history and the export
   * set — so a drop that lands on an open project asks twice: once about the unsaved clip (the one
   * wording of that question lives in `confirmDiscardDirtyClip`), once about the project itself.
   * With nothing open there is nothing to lose and nothing to ask.
   */
  async function importDroppedGlb(file: File): Promise<void> {
    const project = store.getState().project;
    if (project) {
      if (!(await confirmDiscardDirtyClip(store))) return;
      const confirmed = await dialogs.confirm({
        title: 'Import a new character?',
        message: `"${file.name}" will open as a new project; "${project.name}" stays saved but will be closed.`,
        confirmLabel: 'Import',
      });
      if (!confirmed) return;
    }
    runAsync(controllers.project.importGlbFile(file, file.name.replace(/\.glb$/i, '')));
  }

  const onDragOver = (event: DragEvent): void => {
    if (!isFileDrag(event) || isDropTextTarget(event.target)) return;
    // Without this the browser navigates away to the dropped file — the default that turns a
    // mis-aimed drop into "the editor closed and my work is gone".
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    showDropHint();
  };

  const onDrop = (event: DragEvent): void => {
    // Not a file drag, or a drag into a text field: not ours. Returning WITHOUT preventDefault is
    // the point — text dropped into the project-name input has to reach the input.
    if (!isFileDrag(event) || isDropTextTarget(event.target)) return;
    event.preventDefault();
    clearDropHint();
    const target = event.target;
    if (target instanceof Element && target.closest('.dropzone')) return;
    const file = firstGlb(event.dataTransfer?.files);
    if (!file) {
      store.actions.toast('info', 'Drop a .glb file to import a model');
      return;
    }
    void importDroppedGlb(file);
  };

  window.addEventListener('dragover', onDragOver);
  window.addEventListener('drop', onDrop);

  /**
   * Global shortcuts. Space / K / Delete / arrows belong to the timeline and are NOT bound here.
   */
  function onKeyDown(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    if (isTextEntry(event.target)) return;
    const key = event.key.toLowerCase();

    if (key === 'z' && !event.shiftKey) {
      event.preventDefault();
      store.actions.undo();
    } else if ((key === 'z' && event.shiftKey) || key === 'y') {
      event.preventDefault();
      store.actions.redo();
    } else if (key === 's') {
      event.preventDefault();
      if (event.shiftKey) {
        // Ctrl+Shift+S is "save as" everywhere else; before this it fell through to a plain save,
        // which silently did the opposite of what the user asked. It runs the toolbar button's own
        // flow — the name dialog included — so the shortcut and the button cannot drift into two
        // features with one name.
        promptSaveClipAs(store, controllers);
      } else {
        controllers.library.saveCurrentClip().catch(() => {
          /* the controller already reported it */
        });
      }
    }
  }

  document.addEventListener('keydown', onKeyDown);

  return {
    containers,
    layout,
    dispose() {
      if (disposed) return;
      disposed = true;
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      clearDropHint();
      unsubscribeMode();
      for (const panel of mounted.splice(0)) panel.dispose();
      mounted = [];
      notifications.dispose();
      toolbar.dispose();
      layout.dispose();
      rootEl.classList.remove('shell', 'no-timeline');
      rootEl.replaceChildren();
    },
  };
}
